import { getPepHealthCheckDisplay, isPepPartialSetup } from './parsePepHealthCheckLastResult'
import {
  getEffectivePmsReportsInLast48Hours,
  isPmsSyncLastInfoStale
} from './pmsSyncDataRecency'
import {
  PEP_CREDENTIAL_ISSUE_KEYS,
  resolvePepCredentialIssueFromRow
} from './classifyPepCredentialFailureReason'

export const PEP_CSM_ACTION_KEYS = {
  none: 'none',
  installApp: 'installApp',
  partialSetup: 'partialSetup',
  checkPcOnline: 'checkPcOnline',
  dashboardLogin: 'dashboardLogin',
  pepLogin: 'pepLogin',
  escalateEngineering: 'escalateEngineering',
  hiltonAccess: 'hiltonAccess',
  wrongPassword: 'wrongPassword',
  changePassword: 'changePassword',
  pmsSyncIssue: 'pmsSyncIssue'
}

/** Lower rank = higher priority in default sort. */
export const PEP_CSM_ACTION_SORT_ORDER = {
  [PEP_CSM_ACTION_KEYS.escalateEngineering]: 0,
  [PEP_CSM_ACTION_KEYS.hiltonAccess]: 1,
  [PEP_CSM_ACTION_KEYS.wrongPassword]: 2,
  [PEP_CSM_ACTION_KEYS.changePassword]: 3,
  [PEP_CSM_ACTION_KEYS.installApp]: 4,
  [PEP_CSM_ACTION_KEYS.partialSetup]: 5,
  [PEP_CSM_ACTION_KEYS.checkPcOnline]: 6,
  [PEP_CSM_ACTION_KEYS.dashboardLogin]: 7,
  [PEP_CSM_ACTION_KEYS.pepLogin]: 8,
  [PEP_CSM_ACTION_KEYS.pmsSyncIssue]: 9,
  [PEP_CSM_ACTION_KEYS.none]: 10
}

const PEP_CSM_ACTION_DEFINITIONS = {
  [PEP_CSM_ACTION_KEYS.none]: {
    label: 'No action needed',
    instruction: 'No CSM action required — hotel is operating normally.',
    severity: 'success',
    reason: 'PEP health and sync look good.'
  },
  [PEP_CSM_ACTION_KEYS.installApp]: {
    label: 'Install app',
    instruction:
      'Send the PEP desktop installer and confirm a hotel user installs it and signs in to Directful.',
    severity: 'warn',
    reason: 'No desktop app telemetry has been received for this hotel.'
  },
  [PEP_CSM_ACTION_KEYS.partialSetup]: {
    label: 'Partial setup',
    instruction:
      'Some hotel users are set up, but others still do not have the PEP desktop app installed. Send the installer to every remaining user on the roster, have each install it on their PC, sign in to Directful, and complete Hilton PEP sign-in. Open View details to see which users show No app signal.',
    severity: 'warn',
    reason:
      'At least one hotel user has the app, but other dashboard users on the roster still need installation or sign-in.'
  },
  [PEP_CSM_ACTION_KEYS.checkPcOnline]: {
    label: 'Check PC is on',
    instruction:
      'Ask the hotel to turn the PC on, connect to the internet, and leave the Directful desktop app running. Do not reinstall.',
    severity: 'warn',
    reason: 'Desktop telemetry or PMS sync is stale — the PC may be off or the app closed.'
  },
  [PEP_CSM_ACTION_KEYS.dashboardLogin]: {
    label: 'Dashboard sign-in',
    instruction:
      'Ask a hotel user to open the Directful desktop app and sign in to the Directful dashboard.',
    severity: 'warn',
    reason: 'Onboarding is pending or dashboard sign-in is required.'
  },
  [PEP_CSM_ACTION_KEYS.pepLogin]: {
    label: 'Hilton PEP sign-in',
    instruction:
      'Ask a hotel user to open the desktop app and complete Hilton PEP sign-in when prompted.',
    severity: 'warn',
    reason: 'Hilton PEP sign-in is not verified on the latest device signals.'
  },
  [PEP_CSM_ACTION_KEYS.escalateEngineering]: {
    label: 'Escalate to engineering',
    instruction:
      'Escalate to engineering. PEP sign-in may be active but data is not flowing — check property configuration and re-login if advised.',
    severity: 'error',
    reason: 'Data is flowing partially but PEP authorization or data pull is failing.'
  },
  [PEP_CSM_ACTION_KEYS.hiltonAccess]: {
    label: 'Hilton access grant',
    instruction:
      'This is a Hilton-side access grant — re-login will not fix it. Ask Hilton to enable PEP reservation-search for this property.',
    severity: 'error',
    reason: 'Health Check reports a PEP authorization problem with onboarding complete.'
  },
  [PEP_CSM_ACTION_KEYS.wrongPassword]: {
    label: 'Wrong password',
    instruction:
      'Hilton rejected the stored PEP username or password. Open View details, confirm the failing credential, and update the password in PEP login credentials.',
    severity: 'error',
    reason: 'Stored PEP credential failed Hilton sign-in with wrong password.'
  },
  [PEP_CSM_ACTION_KEYS.changePassword]: {
    label: 'Change password',
    instruction:
      'Hilton requires a password change (Password Expiring / expired). Open View details, update the stored PEP password after the hotel changes it in Hilton, then retry.',
    severity: 'error',
    reason: 'Stored PEP credential needs a Hilton password change.'
  },
  [PEP_CSM_ACTION_KEYS.pmsSyncIssue]: {
    label: 'PMS sync issue',
    instruction:
      'Review PMS sync status and credentials. Use Test login or contact support if sync remains broken.',
    severity: 'warn',
    reason: 'PMS sync is disconnected, stale, or reporting problems.'
  }
}

/** Action keys shown in the Health Check PEP CSM action column (and filter dropdown). */
export const PEP_CSM_ACTION_COLUMN_KEYS = [
  PEP_CSM_ACTION_KEYS.escalateEngineering,
  PEP_CSM_ACTION_KEYS.hiltonAccess,
  PEP_CSM_ACTION_KEYS.wrongPassword,
  PEP_CSM_ACTION_KEYS.changePassword,
  PEP_CSM_ACTION_KEYS.installApp,
  PEP_CSM_ACTION_KEYS.partialSetup,
  PEP_CSM_ACTION_KEYS.checkPcOnline,
  PEP_CSM_ACTION_KEYS.dashboardLogin,
  PEP_CSM_ACTION_KEYS.pepLogin,
  PEP_CSM_ACTION_KEYS.none
]

/** Filter dropdown options — same labels as the CSM action column. */
export const PEP_CSM_ACTION_FILTER_OPTIONS = PEP_CSM_ACTION_COLUMN_KEYS.reduce(
  (options, actionKey) => {
    options[actionKey] = PEP_CSM_ACTION_DEFINITIONS[actionKey].label
    return options
  },
  {}
)

export function getPepCsmActionFilterLabel(actionKey) {
  return (
    PEP_CSM_ACTION_FILTER_OPTIONS[actionKey] ||
    PEP_CSM_ACTION_DEFINITIONS[actionKey]?.label ||
    actionKey
  )
}

/** Drops filter values that never appear in the PEP CSM action column. */
export function sanitizePepCsmActionFilterKeys(selectedActionKeys) {
  if (!Array.isArray(selectedActionKeys)) {
    return []
  }

  const allowedKeys = new Set(PEP_CSM_ACTION_COLUMN_KEYS)
  return selectedActionKeys.filter(key => allowedKeys.has(key))
}

function buildAction(actionKey, reasonOverride, labelOverride) {
  const definition = PEP_CSM_ACTION_DEFINITIONS[actionKey] || PEP_CSM_ACTION_DEFINITIONS.none
  return {
    actionKey,
    actionLabel: labelOverride || definition.label,
    actionInstruction: definition.instruction,
    severity: definition.severity,
    reason: reasonOverride || definition.reason
  }
}

function resolveCredentialFailureReasonText(row) {
  return (
    row?.pepCredentialLastFailureReason ||
    row?.PepCredentialLastFailureReason ||
    ''
  )
}

function resolveCredentialWorkingCount(row) {
  return (
    Number(row?.pepCredentialWorkingCount ?? row?.PepCredentialWorkingCount ?? 0) || 0
  )
}

function formatCredentialIssueActionLabel(baseLabel, workingCount, issueCount, expireDays) {
  const working = Number(workingCount) || 0
  const issue = Number(issueCount) || 0
  let label = baseLabel
  if (working > 0) {
    label = `${baseLabel} (${working}/${issue})`
  }
  if (expireDays !== null && expireDays !== undefined && Number.isFinite(Number(expireDays))) {
    const days = Number(expireDays)
    label = `${label} · expires in ${days} day${days === 1 ? '' : 's'}`
  }
  return label
}

/**
 * Derives a single CSM-facing action for a Health Check PMS row.
 * Uses list-level fields only (no telemetry API).
 *
 * @param {object} row
 * @returns {{ actionKey: string, actionLabel: string, actionInstruction: string, severity: string, reason: string }}
 */
export function derivePepCsmAction(row) {
  const {
    pms,
    lastResult,
    pmsSyncLastInfoReceivedOn,
    pmsSyncTotalReportsReceivedInLast48Hours,
    isHavingProblems,
    pmsSyncIsConnected,
    isDisabled
  } = row || {}

  if (pms !== 'pep') {
    const syncStale = isPmsSyncLastInfoStale(pmsSyncLastInfoReceivedOn)
    if (
      pmsSyncIsConnected === false ||
      isHavingProblems === true ||
      (!isDisabled && syncStale)
    ) {
      return buildAction(PEP_CSM_ACTION_KEYS.pmsSyncIssue)
    }
    return buildAction(PEP_CSM_ACTION_KEYS.none)
  }

  const reportsInLast48Hours = getEffectivePmsReportsInLast48Hours(
    pmsSyncLastInfoReceivedOn,
    pmsSyncTotalReportsReceivedInLast48Hours
  )
  const syncStale = isPmsSyncLastInfoStale(pmsSyncLastInfoReceivedOn)
  const pepDisplay = getPepHealthCheckDisplay(lastResult, pms, reportsInLast48Hours)

  if (!pepDisplay) {
    return buildAction(PEP_CSM_ACTION_KEYS.installApp)
  }

  const {
    badgeKey,
    issueDevices,
    activeDevices,
    onboardingState,
    futureState,
    isNoTelemetryFallback
  } = pepDisplay

  if (badgeKey === 'AppNotInstalled') {
    return buildAction(PEP_CSM_ACTION_KEYS.installApp)
  }

  if (isNoTelemetryFallback && reportsInLast48Hours === 0 && activeDevices === 0) {
    return buildAction(PEP_CSM_ACTION_KEYS.installApp)
  }

  if (badgeKey === 'PepAuthorizationError') {
    if (reportsInLast48Hours > 0 && issueDevices > 0) {
      return buildAction(PEP_CSM_ACTION_KEYS.escalateEngineering)
    }
    if (onboardingState === 'Done' && futureState === 'Active') {
      return buildAction(PEP_CSM_ACTION_KEYS.hiltonAccess)
    }
    // Fall through so wrong/change password can surface when authorization badge is set.
  }

  const credentialIssue = resolvePepCredentialIssueFromRow(row)
  const workingCount = resolveCredentialWorkingCount(row)
  if (credentialIssue === PEP_CREDENTIAL_ISSUE_KEYS.wrongPassword) {
    const wrongCount =
      Number(
        row?.pepCredentialWrongPasswordCount ?? row?.PepCredentialWrongPasswordCount ?? 0
      ) || 0
    const base = PEP_CSM_ACTION_DEFINITIONS[PEP_CSM_ACTION_KEYS.wrongPassword].label
    return buildAction(
      PEP_CSM_ACTION_KEYS.wrongPassword,
      resolveCredentialFailureReasonText(row) || undefined,
      formatCredentialIssueActionLabel(base, workingCount, wrongCount)
    )
  }
  if (credentialIssue === PEP_CREDENTIAL_ISSUE_KEYS.changePassword) {
    const changeCount =
      Number(
        row?.pepCredentialChangePasswordCount ?? row?.PepCredentialChangePasswordCount ?? 0
      ) || 0
    const expireRaw = row?.pepPasswordExpireDays ?? row?.PepPasswordExpireDays
    const expireDays =
      expireRaw === null || expireRaw === undefined || expireRaw === ''
        ? null
        : Number(expireRaw)
    const base = PEP_CSM_ACTION_DEFINITIONS[PEP_CSM_ACTION_KEYS.changePassword].label
    return buildAction(
      PEP_CSM_ACTION_KEYS.changePassword,
      resolveCredentialFailureReasonText(row) || undefined,
      formatCredentialIssueActionLabel(
        base,
        workingCount,
        changeCount,
        Number.isFinite(expireDays) ? expireDays : null
      )
    )
  }

  if (badgeKey === 'PepAuthorizationError') {
    return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
  }

  if (syncStale) {
    return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
  }

  if (isPepPartialSetup(pepDisplay, row)) {
    return buildAction(PEP_CSM_ACTION_KEYS.partialSetup)
  }

  if (badgeKey === 'NeedsAttention' && activeDevices === 0) {
    return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
  }

  if (badgeKey === 'NeedsAttention') {
    if (onboardingState === 'Pending') {
      return buildAction(PEP_CSM_ACTION_KEYS.dashboardLogin)
    }
    if (issueDevices > 0 && reportsInLast48Hours === 0) {
      return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
    }
    if (futureState === 'Inactive') {
      return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
    }
    if (issueDevices > 0) {
      return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
    }
  }

  if (badgeKey === 'Working') {
    if (issueDevices === 0) {
      if (isPepPartialSetup(pepDisplay, row)) {
        return buildAction(PEP_CSM_ACTION_KEYS.partialSetup)
      }
      return buildAction(PEP_CSM_ACTION_KEYS.none)
    }
    if (onboardingState === 'Pending') {
      return buildAction(PEP_CSM_ACTION_KEYS.dashboardLogin)
    }
    if (futureState === 'Inactive') {
      return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
    }
    return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
  }

  if (isPepPartialSetup(pepDisplay, row)) {
    return buildAction(PEP_CSM_ACTION_KEYS.partialSetup)
  }

  return buildAction(PEP_CSM_ACTION_KEYS.none)
}

export function resolvePepCsmActionSortRank(row) {
  const action = derivePepCsmAction(row)
  return PEP_CSM_ACTION_SORT_ORDER[action.actionKey] ?? Number.MAX_SAFE_INTEGER
}

/**
 * @param {string} actionKey
 * @param {string[]} selectedActionKeys - empty means no filter
 */
export function matchesPepCsmActionFilter(actionKey, selectedActionKeys) {
  if (!selectedActionKeys || selectedActionKeys.length === 0) {
    return true
  }
  return selectedActionKeys.includes(actionKey)
}

/**
 * Counts CSM actions for the current filtered hotel list.
 * @param {object[]} hotels
 */
export function buildPepCsmActionSummary(hotels) {
  const counts = Object.values(PEP_CSM_ACTION_KEYS).reduce((acc, key) => {
    acc[key] = 0
    return acc
  }, {})

  for (const hotel of hotels || []) {
    const { actionKey } = derivePepCsmAction(hotel)
    counts[actionKey] = (counts[actionKey] || 0) + 1
  }

  const total = (hotels || []).length
  const needingAction = total - (counts[PEP_CSM_ACTION_KEYS.none] || 0)

  return { counts, total, needingAction }
}

/** Queue chips shown in the summary bar (excludes none and pmsSyncIssue). */
export const PEP_CSM_ACTION_SUMMARY_KEYS = [
  PEP_CSM_ACTION_KEYS.wrongPassword,
  PEP_CSM_ACTION_KEYS.changePassword,
  PEP_CSM_ACTION_KEYS.installApp,
  PEP_CSM_ACTION_KEYS.partialSetup,
  PEP_CSM_ACTION_KEYS.checkPcOnline,
  PEP_CSM_ACTION_KEYS.dashboardLogin,
  PEP_CSM_ACTION_KEYS.pepLogin,
  PEP_CSM_ACTION_KEYS.escalateEngineering,
  PEP_CSM_ACTION_KEYS.hiltonAccess
]
